import { omit, some } from 'lodash'
import { isColorLight } from '@/utils/color.utils'
import { nullifiedThemeEmptyField } from '@/vuex/modules/presentation/theme.module'
import { DEFAULT_THEME_ATTRIBUTES } from '@/constant/theme'
import { isFeatureEnabled, getVariant } from '@/vuex/modules/featureToggles.module'

// The generating Xl image size was commited in 2021/11/11
// So I think to be cautious, I would pick 3 months after the commit date, to make sure it's already released.
// After that date, all slide image has a xl version.
// More details can be found here:
// https://github.com/AhaSlides-Product/stpancras-general-api/commit/2719de7b47a7116a5f4391d8ccae641133727b56#diff-bbf3d5376b42e13eb463111ac442ef293a5f6db76a59057efa9a766522adcde1
const releaseXLargeImageFeatureDate = '2022-02-11'

const _getters = {
  canViewThemeGallery: (_, __, ___, rootGetters) => {
    return rootGetters['themeModule/canViewThemeGallery']
  },
  canUseXXLImage: (_, getters, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-xxl-slide-image') && getters.slideHasXlargeImage
  },
  slideHasXlargeImage: (_, getters) => {
    const slideCreatedDate = new Date(getters.slideActive?.createdAt)
    const dayStartUsingXLargeFeature = new Date(getters.dayStartUsingXLargeFeature)

    return slideCreatedDate > dayStartUsingXLargeFeature
  },
  dayStartUsingXLargeFeature: (_, __, ___, rootGetters) => {
    // The variant is for easier for us to config it in Unleash later :))
    const { payload } = getVariant(rootGetters, 'release-xxl-slide-image')
    const value = payload?.value || null
    const content = JSON.parse(value)

    return content?.startDate || releaseXLargeImageFeatureDate
  },
  presentationTheme: (_, __, ___, rootGetters) => {
    const theme = rootGetters['themeModule/currentPresentationTheme']
    // We doesn't need the backgroundImage and baseColour because it will be rendered in another layer
    return omit(theme, ['backgroundImage', 'baseColour', 'visibility'])
  },
  // Slide depends on the theme except the backgroundImage, baseColour and visibility
  slides: (_, getters, rootState) => {
    const { slides } = rootState?.presentation
    const { presentationTheme } = getters
    if (getters.canViewThemeGallery) {
      return slides.map((slide) => {
        return {
          ...presentationTheme,
          ...nullifiedThemeEmptyField(slide),
        }
      })
    }

    return slides.map((slide) => {
      return {
        ...DEFAULT_THEME_ATTRIBUTES,
        ...nullifiedThemeEmptyField(slide),
      }
    })
  },
  slideActive: (_, getters, rootState) => {
    const slideActive = rootState?.slideActive
    const { presentationTheme } = getters

    if (getters.canViewThemeGallery) {
      return {
        ...presentationTheme,
        ...nullifiedThemeEmptyField(slideActive),
      }
    }

    return {
      ...DEFAULT_THEME_ATTRIBUTES,
      ...nullifiedThemeEmptyField(slideActive),
    }
  },
  // This one doesn't use the omitted version of the theme
  // Only use for calculating this.boxWaitBg in QuizSlideContent.vue
  slideActiveBaseColour: (_, getters, rootState, rootGetters) => {
    const { baseColour } = rootState?.slideActive
    const presentationTheme = rootGetters['themeModule/currentPresentationTheme']

    if (getters.canViewThemeGallery) {
      return baseColour || presentationTheme.baseColour
    }

    return baseColour
  },
  hasCustomisedSlide: (_, __, rootState) => {
    const { slides } = rootState?.presentation
    return some(slides, (item) => {
      const slide = nullifiedThemeEmptyField(item)
      return slide.textColour || slide.baseColour || slide.backgroundImage
    })
  },
  defaultSlideConfig: (_, getters) => {
    if (getters.canViewThemeGallery) return {}
    return omit(DEFAULT_THEME_ATTRIBUTES, ['backgroundImage'])
  },
  modalBackgroundColour: (_, getters) => {
    const { slideActiveBaseColour, slideActive } = getters
    const { textColour } = slideActive
    if (getters.canViewThemeGallery) {
      // To make sure it always contrast with the text colour
      return isColorLight(textColour)
        ? DEFAULT_THEME_ATTRIBUTES.textColour
        : DEFAULT_THEME_ATTRIBUTES.baseColour
    }
    return slideActiveBaseColour
  },
  blankStyleSlide: (_, getters) => {
    if (getters.canViewThemeGallery) {
      return {
        textColour: '',
        baseColour: '',
        visibility: -1,
        backgroundImage: null,
      }
    }
    return {
      textColour: '#313131',
      baseColour: '#ffffff',
      visibility: 20,
      backgroundImage: null,
    }
  },
  isLegacyBackgroundImageThemeUrl(_, getters) {
    return getters?.slideActive?.backgroundImage?.large?.includes('themes/')
  },
  isLegacySlideDocumentImageUrl(_, getters) {
    return !getters?.slideActive?.image?.large?.includes('static/')
  },
}

export default {
  namespaced: true,
  getters: _getters,
}
